import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaChartBar,
  FaCog,
  FaCommentDots,
  FaEllipsisH,
  FaEnvelope,
  FaExternalLinkAlt,
  FaEye,
  FaFolderOpen,
  FaRegFileAlt,
  FaUsers,
  FaWrench,
} from 'react-icons/fa';
import { LuCalendar, LuClock3, LuTrendingUp } from 'react-icons/lu';
import { api, getApiData } from '../api';
import { useAdminAuth } from '../context/AdminAuthContext';

const cardClass =
  'rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_16px_rgba(15,23,42,0.04)] dark:border-white/10 dark:bg-[#10151d] dark:shadow-[0_2px_16px_rgba(0,0,0,0.2)]';

const segmentColors = ['#f3c715', '#38bdf8', '#f3c715', '#a78bfa'];

const categoryStyles = {
  multimedia: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'e-commerce': 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  'web app': 'bg-primary/15 text-slate-800 dark:bg-primary/20 dark:text-primary',
  default: 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-gray-300',
};

const statCardThemes = {
  totalProjects: {
    iconBg: 'bg-primary/15 text-primary',
    trend: '12%',
    trendUp: true,
    subtitle: 'Total Projects',
  },
  totalMessages: {
    iconBg: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300',
    trend: '8%',
    trendUp: true,
    subtitle: 'Total Messages',
  },
  totalServices: {
    iconBg: 'bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300',
    trend: '5%',
    trendUp: true,
    subtitle: 'Total Services',
  },
  totalBlogs: {
    iconBg: 'bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300',
    trend: '0%',
    trendUp: false,
    subtitle: 'Total Blogs',
  },
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return formatDate(value);
}

function getInitials(firstName = '', lastName = '') {
  const first = firstName.trim()[0] || '';
  const last = lastName.trim()[0] || '';
  return (first + last).toUpperCase() || 'RT';
}

function getProjectProgress(project) {
  const fields = [project.title, project.category, project.description, project.mainImageUrl, project.slug];
  const filled = fields.filter(Boolean).length;
  return Math.round((filled / fields.length) * 100);
}

function getCategoryClass(category = '') {
  const key = category.trim().toLowerCase();
  return categoryStyles[key] || categoryStyles.default;
}

function formatDuration(seconds = 0) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

function formatPageDisplay(item) {
  const path = item.path ?? item.Path ?? '';
  const label = item.label ?? item.Label ?? '';
  if (path === '/' || label === 'Home') return 'Home /';
  if (path) return path;
  return label || 'Unknown page';
}

function normalizeVisitorAnalytics(raw) {
  if (!raw) return null;

  const monthly = raw.monthlyVisitors ?? raw.MonthlyVisitors ?? [];
  const topPages = raw.topPages ?? raw.TopPages ?? [];
  const topServices = raw.topServices ?? raw.TopServices ?? [];
  const returning = raw.topReturningVisitors ?? raw.TopReturningVisitors ?? [];

  return {
    totalVisitors: raw.totalVisitors ?? raw.TotalVisitors ?? 0,
    totalPageViews: raw.totalPageViews ?? raw.TotalPageViews ?? 0,
    visitsToday: raw.visitsToday ?? raw.VisitsToday ?? 0,
    averageDailyVisitors: raw.averageDailyVisitors ?? raw.AverageDailyVisitors ?? 0,
    monthlyVisitors: monthly.map((item) => ({
      label: item.label ?? item.Label ?? '',
      value: item.value ?? item.Value ?? 0,
    })),
    topPages: topPages.map((item) => ({
      label: item.label ?? item.Label ?? '',
      path: item.path ?? item.Path ?? '',
      views: item.views ?? item.Views ?? 0,
      avgDurationSeconds: item.avgDurationSeconds ?? item.AvgDurationSeconds ?? 0,
    })),
    topServices: topServices.map((item) => ({
      label: item.label ?? item.Label ?? '',
      slug: item.slug ?? item.Slug ?? '',
      views: item.views ?? item.Views ?? 0,
      avgDurationSeconds: item.avgDurationSeconds ?? item.AvgDurationSeconds ?? 0,
    })),
    topReturningVisitors: returning.map((item) => ({
      visitorLabel: item.visitorLabel ?? item.VisitorLabel ?? '',
      visitCount: item.visitCount ?? item.VisitCount ?? 0,
      lastVisitedAt: item.lastVisitedAt ?? item.LastVisitedAt ?? null,
    })),
  };
}

function DonutChart({ segments, total }) {
  const safeTotal = segments.reduce((sum, item) => sum + item.value, 0) || 1;
  let cursor = 0;

  const gradient = segments
    .map((segment) => {
      const start = (cursor / safeTotal) * 100;
      cursor += segment.value;
      const end = (cursor / safeTotal) * 100;
      return `${segment.color} ${start}% ${end}%`;
    })
    .join(', ');

  return (
    <div className="flex flex-col items-center py-2">
      <div
        className="relative flex h-44 w-44 items-center justify-center rounded-full"
        style={{ background: `conic-gradient(${gradient})` }}
      >
        <div className="flex h-[118px] w-[118px] flex-col items-center justify-center rounded-full bg-white dark:bg-[#10151d]">
          <span className="text-3xl font-extrabold text-slate-900 dark:text-white">{total}</span>
          <span className="text-xs text-slate-500 dark:text-gray-400">Total Items</span>
        </div>
      </div>
      <div className="mt-5 w-full space-y-3">
        {segments.map((segment) => (
          <div key={segment.label} className="flex items-center justify-between text-sm">
            <span className="inline-flex items-center gap-2.5 text-slate-600 dark:text-gray-300">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
              {segment.label}
            </span>
            <strong className="text-slate-900 dark:text-white">{segment.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildSmoothLinePath(points) {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const midX = (current.x + next.x) / 2;
    path += ` C ${midX} ${current.y}, ${midX} ${next.y}, ${next.x} ${next.y}`;
  }
  return path;
}

function VisitorLineChart({ data }) {
  const fallbackMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const chartData =
    data.length > 0
      ? data
      : fallbackMonths.map((label) => ({
          label,
          value: 0,
        }));

  const width = 640;
  const height = 220;
  const padding = { top: 18, right: 12, bottom: 32, left: 36 };
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const maxValue = Math.max(8, ...chartData.map((item) => item.value));
  const yTicks = Array.from({ length: 5 }, (_, index) => Math.round((maxValue / 4) * index));

  const points = chartData.map((item, index) => {
    const x =
      padding.left + (chartData.length === 1 ? innerWidth / 2 : (index / (chartData.length - 1)) * innerWidth);
    const y = padding.top + innerHeight - (item.value / maxValue) * innerHeight;
    return { x, y, label: item.label, value: item.value };
  });

  const linePath = buildSmoothLinePath(points);
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + innerHeight} L ${points[0].x} ${padding.top + innerHeight} Z`;

  return (
    <div className="rounded-2xl border border-slate-100 bg-gradient-to-b from-slate-50/40 to-white px-2 py-3 dark:border-white/10 dark:from-white/[0.03] dark:to-transparent">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-label="Monthly visitor chart">
        <defs>
          <linearGradient id="visitorAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f3c715" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#f3c715" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = padding.top + innerHeight - (tick / maxValue) * innerHeight;
          return (
            <g key={tick}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e2e8f0"
                strokeDasharray="4 6"
              />
              <text x={padding.left - 8} y={y + 4} textAnchor="end" className="fill-slate-400 text-[10px]">
                {tick}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill="url(#visitorAreaGradient)" />
        <path d={linePath} fill="none" stroke="#f3c715" strokeWidth="3" strokeLinecap="round" />

        {points.map((point) => (
          <g key={point.label}>
            <circle cx={point.x} cy={point.y} r="5" fill="#fff" stroke="#f3c715" strokeWidth="3" />
            <text x={point.x} y={height - 8} textAnchor="middle" className="fill-slate-400 text-[10px]">
              {point.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function VisitorStatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white px-4 py-4 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <Icon size={16} />
      </div>
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:text-gray-500">{label}</p>
      <p className="mt-1 text-[1.75rem] font-extrabold leading-none text-slate-900 dark:text-white">{value}</p>
      <div className="mt-3 h-px w-8 bg-slate-200 dark:bg-white/10" />
    </div>
  );
}

function MessagePaperPlane() {
  return (
    <svg viewBox="0 0 220 120" className="pointer-events-none absolute bottom-0 right-0 h-28 w-44 opacity-90" aria-hidden="true">
      <defs>
        <linearGradient id="messageWaveGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f3c715" />
          <stop offset="100%" stopColor="#f3c715" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M24 88 C 44 78, 68 62, 92 52"
        fill="none"
        stroke="#f3c715"
        strokeWidth="2"
        strokeDasharray="4 6"
        opacity="0.55"
      />
      <path d="M92 52 L 118 44 L 108 68 Z" fill="#f3c715" opacity="0.85" />
      <path d="M0 118 C 40 108, 90 96, 220 118" fill="url(#messageWaveGradient)" opacity="0.35" />
    </svg>
  );
}

function AnalyticsSubCard({ icon: Icon, title, children, footer, className = '' }) {
  return (
    <div className={`${cardClass} flex min-h-[280px] flex-col ${className}`}>
      <div className="mb-4 flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
          <Icon size={15} />
        </div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h3>
      </div>
      <div className="flex-1">{children}</div>
      {footer ? <div className="mt-4 border-t border-slate-100 pt-4 dark:border-white/10">{footer}</div> : null}
    </div>
  );
}

function ServicesEmptyIllustration() {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10">
        <svg viewBox="0 0 80 80" className="h-14 w-14" aria-hidden="true">
          <rect x="16" y="12" width="40" height="52" rx="6" fill="#f3c715" opacity="0.25" />
          <rect x="24" y="22" width="24" height="4" rx="2" fill="#f3c715" opacity="0.55" />
          <rect x="24" y="32" width="18" height="3" rx="1.5" fill="#f3c715" opacity="0.4" />
          <rect x="28" y="48" width="6" height="12" rx="2" fill="#f3c715" />
          <rect x="38" y="42" width="6" height="18" rx="2" fill="#f3c715" opacity="0.75" />
          <rect x="48" y="38" width="6" height="22" rx="2" fill="#f3c715" opacity="0.55" />
        </svg>
      </div>
      <p className="text-sm text-slate-500 dark:text-gray-400">No service page visits recorded yet.</p>
    </div>
  );
}

function AnalyticsFooterLink({ label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 transition hover:text-slate-900 dark:text-gray-300 dark:hover:text-white"
    >
      {label}
      <Icon size={12} className="text-primary" />
    </button>
  );
}

function ProjectIcon({ project }) {
  if (project.mainImageUrl) {
    return (
      <img
        src={project.mainImageUrl}
        alt={project.title}
        className="h-9 w-9 shrink-0 rounded-xl border border-slate-200 object-cover dark:border-white/10"
      />
    );
  }

  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
      <FaFolderOpen size={14} />
    </div>
  );
}

const AdminDashboard = () => {
  const { user } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [visitorAnalytics, setVisitorAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectTab, setProjectTab] = useState('active');
  const [showAllPages, setShowAllPages] = useState(false);
  const [showAllVisitors, setShowAllVisitors] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadDashboardData = async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
          setError('');
        }

        const [dashboardResult, messagesResult, projectsResult, visitorsResult] = await Promise.allSettled([
          api.dashboard(),
          api.list('/api/messages'),
          api.list('/api/projects'),
          api.visitorAnalytics(),
        ]);

        if (!isMounted) return;

        if (dashboardResult.status === 'fulfilled') {
          setStats(getApiData(dashboardResult.value) ?? dashboardResult.value.data);
        } else if (!silent) {
          setStats({
            totalServices: 0,
            totalProjects: 0,
            totalBlogs: 0,
            totalPartners: 0,
            totalMessages: 0,
          });
          setError(dashboardResult.reason?.message || 'Failed to load dashboard stats.');
        }

        if (messagesResult.status === 'fulfilled') {
          const messageItems = getApiData(messagesResult.value) ?? messagesResult.value.data ?? [];
          setMessages(
            messageItems
              .slice()
              .sort((a, b) => new Date(b.createdAt ?? b.CreatedAt) - new Date(a.createdAt ?? a.CreatedAt)),
          );
        }

        if (projectsResult.status === 'fulfilled') {
          const projectItems = getApiData(projectsResult.value) ?? projectsResult.value.data ?? [];
          setProjects(projectItems.slice().reverse());
        }

        if (visitorsResult.status === 'fulfilled') {
          setVisitorAnalytics(
            normalizeVisitorAnalytics(getApiData(visitorsResult.value) ?? visitorsResult.value.data),
          );
        } else if (!silent) {
          setVisitorAnalytics(null);
        }
      } catch (err) {
        if (isMounted && !silent) {
          setError(err.message || 'Failed to load dashboard data.');
        }
      } finally {
        if (isMounted && !silent) {
          setLoading(false);
        }
      }
    };

    loadDashboardData();

    const refreshTimer = window.setInterval(() => {
      loadDashboardData({ silent: true });
    }, 30000);

    const handleFocus = () => {
      loadDashboardData({ silent: true });
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      isMounted = false;
      window.clearInterval(refreshTimer);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const filteredProjects = useMemo(() => {
    if (projectTab === 'featured') return projects.filter((item) => item.isFeatured);
    if (projectTab === 'all') return projects;
    return projects.filter((item) => item.isActive !== false);
  }, [projects, projectTab]);

  const monthlyVisitors = useMemo(
    () => visitorAnalytics?.monthlyVisitors || [],
    [visitorAnalytics],
  );

  const topPages = visitorAnalytics?.topPages || [];
  const topServices = visitorAnalytics?.topServices || [];
  const topReturningVisitors = visitorAnalytics?.topReturningVisitors || [];
  const visiblePages = showAllPages ? topPages : topPages.slice(0, 3);
  const visibleReturningVisitors = showAllVisitors ? topReturningVisitors : topReturningVisitors.slice(0, 3);

  const statSegments = useMemo(
    () => [
      { label: 'Services', value: stats?.totalServices ?? 0, color: segmentColors[0] },
      { label: 'Projects', value: stats?.totalProjects ?? 0, color: segmentColors[1] },
      { label: 'Blogs', value: stats?.totalBlogs ?? 0, color: segmentColors[2] },
      { label: 'Partners', value: stats?.totalPartners ?? 0, color: segmentColors[3] },
    ],
    [stats],
  );

  const summaryCards = useMemo(
    () => [
      { key: 'totalProjects', label: 'Projects', icon: FaFolderOpen },
      { key: 'totalMessages', label: 'Messages', icon: FaEnvelope },
      { key: 'totalServices', label: 'Services', icon: FaWrench },
      { key: 'totalBlogs', label: 'Blogs', icon: FaRegFileAlt },
    ],
    [],
  );

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-slate-500 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const totalContent =
    (stats?.totalServices ?? 0) +
    (stats?.totalProjects ?? 0) +
    (stats?.totalBlogs ?? 0) +
    (stats?.totalPartners ?? 0);

  const firstName = user?.fullName?.split(' ')?.[0] || 'Admin';

  return (
    <div className="min-w-0 space-y-6">
      {error ? (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[1.75rem] font-extrabold tracking-tight text-slate-900 dark:text-white md:text-[2rem]">
            Welcome back, {firstName}
            <span className="ml-2 inline-block" aria-hidden="true">
              👋
            </span>
          </h1>
          <p className="mt-1.5 text-sm text-slate-500 dark:text-gray-400">
            Here is your website overview and the latest updates.
          </p>
        </div>
        <Link
          to="/admin/modules/projects"
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-[#ffe066] px-5 py-2.5 text-sm font-bold text-black shadow-[0_8px_20px_rgba(243,199,21,0.35)] transition hover:brightness-95"
        >
          View all projects
          <FaArrowRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          const theme = statCardThemes[card.key];
          return (
            <div key={card.key} className={`${cardClass} flex items-start justify-between gap-3`}>
              <div className="flex items-start gap-3">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${theme.iconBg}`}>
                  <Icon size={18} />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-gray-500">
                    {card.label}
                  </p>
                  <p className="mt-1 text-3xl font-extrabold leading-none text-slate-900 dark:text-white">
                    {stats?.[card.key] ?? 0}
                  </p>
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-gray-400">{theme.subtitle}</p>
                </div>
              </div>
              <span
                className={[
                  'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold',
                  theme.trendUp
                    ? 'bg-primary/15 text-slate-900 dark:bg-primary/20 dark:text-primary'
                    : 'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-gray-400',
                ].join(' ')}
              >
                {theme.trendUp ? <LuTrendingUp size={12} /> : '—'}
                {theme.trend}
              </span>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.7fr_1fr]">
        <section className={cardClass}>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <FaChartBar size={15} />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Project Summary</h2>
                <p className="text-xs text-slate-500 dark:text-gray-400">Track active and featured portfolio work</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-xl bg-slate-100 p-1 dark:bg-white/5">
                {[
                  { key: 'active', label: 'Active' },
                  { key: 'featured', label: 'Featured' },
                  { key: 'all', label: 'All' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setProjectTab(tab.key)}
                    className={[
                      'rounded-lg px-3.5 py-1.5 text-xs font-bold transition',
                      projectTab === tab.key
                        ? 'bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900'
                        : 'text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-white',
                    ].join(' ')}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <Link
                to="/admin/modules/projects"
                className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 shadow-sm transition hover:border-slate-300 dark:border-white/10 dark:bg-[#121820] dark:text-gray-200"
              >
                Export
              </Link>
            </div>
          </div>

          <div className="max-w-full overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-[0.08em] text-slate-400 dark:border-white/10">
                  <th className="pb-3 pr-4 font-bold">Project</th>
                  <th className="pb-3 pr-4 font-bold">Category</th>
                  <th className="pb-3 pr-4 font-bold">Status</th>
                  <th className="pb-3 pr-4 font-bold">Created</th>
                  <th className="pb-3 font-bold">Progress</th>
                  <th className="pb-3 pl-2 font-bold" aria-hidden="true">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.slice(0, 5).map((project) => {
                  const progress = getProjectProgress(project);
                  const isFeatured = project.isFeatured;
                  const isActive = project.isActive !== false;

                  return (
                    <tr key={project.id} className="border-b border-slate-50 last:border-0 dark:border-white/5">
                      <td className="py-4 pr-4">
                        <div className="flex items-center gap-3">
                          <ProjectIcon project={project} />
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{project.title}</p>
                            <p className="text-xs text-slate-500 dark:text-gray-400">
                              {project.clientName || 'RAADSO Client'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 pr-4">
                        <span
                          className={[
                            'inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold',
                            getCategoryClass(project.category),
                          ].join(' ')}
                        >
                          {project.category || 'General'}
                        </span>
                      </td>
                      <td className="py-4 pr-4">
                        <span
                          className={[
                            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase',
                            isFeatured
                              ? 'bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300'
                              : isActive
                                ? 'bg-primary/15 text-slate-900 dark:bg-primary/20 dark:text-primary'
                                : 'bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-gray-300',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'h-1.5 w-1.5 rounded-full',
                              isFeatured ? 'bg-orange-500' : isActive ? 'bg-primary' : 'bg-slate-400',
                            ].join(' ')}
                          />
                          {isFeatured ? 'Featured' : isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-4 pr-4 text-slate-500 dark:text-gray-400">
                        {formatDate(project.createdAt ?? project.CreatedAt)}
                      </td>
                      <td className="py-4">
                        <div className="flex min-w-[120px] items-center gap-2">
                          <div className="h-2 flex-1 rounded-full bg-slate-100 dark:bg-white/10">
                            <div
                              className={[
                                'h-2 rounded-full',
                                isFeatured ? 'bg-gradient-to-r from-orange-400 to-primary' : 'bg-gradient-to-r from-primary to-[#ffe066]',
                              ].join(' ')}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="w-8 text-xs font-bold text-slate-600 dark:text-gray-300">{progress}%</span>
                        </div>
                      </td>
                      <td className="py-4 pl-2">
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5 dark:hover:text-white"
                          aria-label="Project actions"
                        >
                          <FaEllipsisH size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center text-sm text-slate-500 dark:text-gray-500">
                      No projects found for this filter.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className={cardClass}>
          <div className="mb-2 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-white">Statistics</h2>
              <p className="text-xs text-slate-500 dark:text-gray-400">Content distribution overview</p>
            </div>
            <label className="sr-only" htmlFor="stats-year">
              Statistics year
            </label>
            <select
              id="stats-year"
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 shadow-sm outline-none dark:border-white/10 dark:bg-[#121820] dark:text-gray-200"
              defaultValue={String(new Date().getFullYear())}
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
            </select>
          </div>

          <DonutChart segments={statSegments} total={totalContent} />
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.7fr_1fr]">
        <section className={cardClass}>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <FaUsers size={16} />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Website Visitors</h2>
                <p className="text-xs text-slate-500 dark:text-gray-400">
                  How often people visit, what they view, and where they spend time
                </p>
              </div>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3.5 py-1.5 text-xs font-bold text-slate-900 dark:bg-primary/20 dark:text-primary">
              <LuTrendingUp size={14} />
              {visitorAnalytics?.visitsToday ?? 0} visits today
            </div>
          </div>

          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
            <VisitorStatCard icon={FaUsers} label="Total Visitors" value={visitorAnalytics?.totalVisitors ?? 0} />
            <VisitorStatCard icon={FaEye} label="Page Views" value={visitorAnalytics?.totalPageViews ?? 0} />
            <VisitorStatCard icon={LuCalendar} label="Today" value={visitorAnalytics?.visitsToday ?? 0} />
            <VisitorStatCard icon={LuTrendingUp} label="Avg / Day" value={visitorAnalytics?.averageDailyVisitors ?? 0} />
          </div>

          <VisitorLineChart data={monthlyVisitors} />
        </section>

        <section className={`${cardClass} relative flex min-h-[520px] flex-col overflow-hidden`}>
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <FaCommentDots size={16} />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Recent Messages</h2>
                <p className="text-xs text-slate-500 dark:text-gray-400">Latest contact form submissions</p>
              </div>
            </div>
            <Link
              to="/admin/modules/messages"
              className="rounded-xl bg-gradient-to-r from-primary to-[#ffe066] px-4 py-2 text-xs font-bold text-black shadow-[0_6px_16px_rgba(243,199,21,0.25)] transition hover:brightness-95"
            >
              View all
            </Link>
          </div>

          <div className="relative flex-1 space-y-3 pb-16">
            {messages.slice(0, 5).map((item) => {
              const isRead = item.isRead ?? item.IsRead;
              return (
                <div
                  key={item.id ?? item.Id}
                  className="flex items-start justify-between gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.03]"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-extrabold text-slate-900 dark:text-primary">
                      {getInitials(item.firstName ?? item.FirstName, item.lastName ?? item.LastName)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                        {[item.firstName ?? item.FirstName, item.lastName ?? item.LastName]
                          .filter(Boolean)
                          .join(' ') || 'Unknown sender'}
                      </p>
                      <p className="truncate text-xs text-slate-500 dark:text-gray-400">
                        {(item.subject ?? item.Subject) || 'No subject'}
                      </p>
                      <div className="mt-2 h-px w-10 bg-slate-200 dark:bg-white/10" />
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span
                      className={[
                        'rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                        isRead
                          ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300'
                          : 'bg-primary/20 text-slate-900 dark:text-primary',
                      ].join(' ')}
                    >
                      {isRead ? 'Read' : 'New'}
                    </span>
                    <span className="text-[11px] text-slate-400 dark:text-gray-500">
                      {formatTimeAgo(item.createdAt ?? item.CreatedAt)}
                    </span>
                  </div>
                </div>
              );
            })}
            {messages.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500 dark:text-gray-500">No messages yet.</p>
            ) : null}
            <MessagePaperPlane />
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <AnalyticsSubCard
          icon={FaRegFileAlt}
          title="Top Pages"
          footer={
            topPages.length > 3 ? (
              <AnalyticsFooterLink
                label={showAllPages ? 'Show less' : 'View all pages'}
                icon={FaExternalLinkAlt}
                onClick={() => setShowAllPages((value) => !value)}
              />
            ) : null
          }
        >
          {topPages.length > 0 ? (
            <div className="space-y-4">
              {visiblePages.map((item) => (
                <div key={`${item.path}-${item.label}`} className="flex items-start justify-between gap-3">
                  <p className="min-w-0 truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {formatPageDisplay(item)}
                  </p>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-bold text-slate-900 dark:text-white">{item.views} views</p>
                    <p className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-gray-400">
                      <LuClock3 size={11} />
                      {formatDuration(item.avgDurationSeconds)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-gray-500">
              No visitor data yet. Browse the public website to start tracking.
            </p>
          )}
        </AnalyticsSubCard>

        <AnalyticsSubCard icon={FaCog} title="Top Services">
          {topServices.length > 0 ? (
            <div className="space-y-4">
              {topServices.map((item) => (
                <div key={item.slug} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.label}</p>
                    <p className="truncate text-[11px] text-slate-500 dark:text-gray-400">{item.slug}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-bold text-slate-900 dark:text-white">{item.views} views</p>
                    <p className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-gray-400">
                      <LuClock3 size={11} />
                      {formatDuration(item.avgDurationSeconds)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ServicesEmptyIllustration />
          )}
        </AnalyticsSubCard>

        <AnalyticsSubCard
          icon={FaUsers}
          title="Most Returning Visitors"
          footer={
            topReturningVisitors.length > 3 ? (
              <AnalyticsFooterLink
                label={showAllVisitors ? 'Show less' : 'View all visitors'}
                icon={FaUsers}
                onClick={() => setShowAllVisitors((value) => !value)}
              />
            ) : null
          }
        >
          {topReturningVisitors.length > 0 ? (
            <div className="space-y-4">
              {visibleReturningVisitors.map((item) => (
                <div key={item.visitorLabel} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{item.visitorLabel}</p>
                    <p className="text-[11px] text-slate-500 dark:text-gray-400">
                      Last visit {formatDate(item.lastVisitedAt)}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-bold text-slate-900 dark:text-primary">
                    {item.visitCount} visits
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-gray-500">No returning visitors yet.</p>
          )}
        </AnalyticsSubCard>
      </div>
    </div>
  );
};

export default AdminDashboard;
